import { useEffect, useMemo, useState } from "react";
import { Card, Drawer, Tag, Tooltip } from "antd";
import { CaretDownFilled, CaretUpFilled } from "@ant-design/icons";
import TableLZM from "../../../../components/design/table/TableLZM";
import { DOT_COLOR } from "../_common/MarkerGeoGrid";
import { MissingOwnBusinessNotice } from "../_common/MissingOwnBusinessNotice";
import { getDistanceProximity, getOpenStatusLabel, OpenStatusColorBadge } from "../_common/RankingCardUtils";

const DiffLabel = ({ diff, solid }: { diff: number, solid?: boolean }) => {
    const color = diff < 0 ? 'DarkRed' : 'darkgreen';
    const Icon = diff > 0 ? CaretUpFilled : CaretDownFilled;
    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            fontSize: 10,
            fontWeight: 700,
            lineHeight: '16px',
            padding: '0 5px',
            borderRadius: 10,
            color: solid ? 'white' : color,
            background: solid ? color : 'white',
            border: solid ? 'none' : `1px solid ${color}`,
        }}>
            <Icon style={{ fontSize: 10 }} />
            {Math.abs(diff)}
        </span>
    );
};

const formatOrdinal = (value: number) => {
    if (value == null || value > 20) return '>20º';
    return `${value}º`;
};

const getPositionDiffInterpretation = (record: any) => {
    const popularityPos = record.positionPopularity;
    const distancePos = record.positionDistance;
    const diff = record.positionDiff;
    if (popularityPos == null || distancePos == null) return null;

    if (diff == null || diff === 0 || popularityPos === distancePos) {
        return (
            <>
                Es el <b>{formatOrdinal(distancePos)}</b> más cercano a las coordenadas
                desde donde se realiza la búsqueda y Google lo muestra en la misma posición{' '}
                (<b>{formatOrdinal(popularityPos)}</b>), lo que significa que su posicionamiento
                es coherente con la distancia.
            </>
        );
    }

    if (diff < 0) {
        const n = Math.abs(diff);
        const posiciones = n === 1 ? 'posición' : 'posiciones';
        return (
            <>
                Es el <b>{formatOrdinal(distancePos)}</b> más cercano a las coordenadas
                desde donde se realiza la búsqueda, Google lo muestra en la posición{' '}
                <b>{formatOrdinal(popularityPos)}</b>, es decir pierde <b>{n}</b> {posiciones}.
            </>
        );
    }

    return (
        <>
            Es el <b>{formatOrdinal(distancePos)}</b> más cercano a las coordenadas
            desde donde se realiza la búsqueda, Google lo muestra en la posición{' '}
            <b>{formatOrdinal(popularityPos)}</b>, es decir gana <b>{diff}</b>{' '}
            {diff === 1 ? 'posición' : 'posiciones'}.
        </>
    );
};

const BusinessInfoTooltipContent = ({ record }: { record: any }) => {
    const interpretation = getPositionDiffInterpretation(record);
    const meters = Number(record.distanceToPoint ?? 0);
    const hasWebsite = record.hasWebsite ?? !!record.websiteUri;
    const openLabel = getOpenStatusLabel(record);
    const rating = record.rating > 0 ? Math.round(10 * record.rating) / 10 : null;
    const reviews = record.userRatingsTotal > 0 ? record.userRatingsTotal : null;

    const rowStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 4,
    } as const;

    return (
        <div style={{ maxWidth: 320, lineHeight: 1.45 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{record.name}</div>

            <div style={{
                padding: '8px 10px',
                borderRadius: 8,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                marginBottom: interpretation ? 10 : 0,
            }}>
                <div style={rowStyle}>
                    <span style={{ opacity: 0.75 }}>Horario</span>
                    <span style={{ fontWeight: 600 }}>{openLabel}</span>
                </div>
                <div style={rowStyle}>
                    <span style={{ opacity: 0.75 }}>Reseñas</span>
                    <span style={{ fontWeight: 600 }}>
                        {rating != null
                            ? `${rating}${reviews != null ? ` · ${reviews}` : ''}`
                            : 'Sin reseñas'}
                    </span>
                </div>
                {record.category && (
                    <div style={rowStyle}>
                        <span style={{ opacity: 0.75 }}>Categoría</span>
                        <span style={{ fontWeight: 600, textAlign: 'right' }}>{record.category}</span>
                    </div>
                )}
                <div style={rowStyle}>
                    <span style={{ opacity: 0.75 }}>Web</span>
                    <span style={{ fontWeight: 600 }}>{hasWebsite ? 'Tiene web' : 'Sin web'}</span>
                </div>
                {meters > 0 && (
                    <div style={rowStyle}>
                        <span style={{ opacity: 0.75 }}>Distancia</span>
                        <span style={{ fontWeight: 600 }}>{meters.toFixed(0)} mts.</span>
                    </div>
                )}
            </div>

            {interpretation && (
                <div style={{ opacity: 0.95 }}>
                    {interpretation}
                </div>
            )}
        </div>
    );
};

const GeogridPointTable = (props: { point: any }) => {
    const places = props.point?.places || [];
    const pageSize = 20;

    const ownIndex = useMemo(
        () => places.findIndex((place: any) => place.reference),
        [places]
    );

    const [page, setPage] = useState(() =>
        ownIndex >= 0 ? Math.floor(ownIndex / pageSize) + 1 : 1
    );

    useEffect(() => {
        if (ownIndex >= 0) {
            setPage(Math.floor(ownIndex / pageSize) + 1);
        } else {
            setPage(1);
        }
    }, [ownIndex, props.point]);

    useEffect(() => {
        if (ownIndex < 0) return;
        const timer = window.setTimeout(() => {
            document
                .querySelector('.geogrid-point-table tr.own-business-row')
                ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 80);
        return () => window.clearTimeout(timer);
    }, [page, ownIndex, places]);

    return (
        <TableLZM
            rowKey={(record: any) => `${record.positionPopularity}-${record.name}-${record.googleMapsUri}`}
            size='small'
            pagination={{
                pageSize,
                current: page,
                onChange: setPage,
            }}
            columns={SEO_COLUMNS}
            dataSource={places}
            rowClassName={(record: any) => record.reference ? 'own-business-row' : ''}
            onRow={(record: any) => {
                if (!record.reference) return {};
                const color = DOT_COLOR[record.positionPopularity] || 'silver';
                return {
                    style: {
                        backgroundColor: `color-mix(in srgb, ${color} 16%, white)`,
                        boxShadow: `inset 5px 0 0 ${color}, inset -5px 0 0 ${color}`,
                    },
                };
            }}
        />
    );
};

const OwnBusinessMissingAlert = ({ point, keyword }: { point: any, keyword?: string }) => {
    const places = point?.places || [];
    const missing = places.length > 0 && !places.some((place: any) => place.reference);
    if (!missing) return null;
    return <MissingOwnBusinessNotice keyword={keyword} style={{ marginBottom: 10 }} />;
};

export const GeogridPointPanel = (props: { point: any, keyword?: string }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '70vh' }}>
            <div style={{
                flexShrink: 0,
                marginBottom: 16,
                zIndex: 2,
            }}>
                <h3 style={{
                    margin: 0,
                    color: '#334155',
                    fontSize: 15,
                    fontWeight: 700,
                    lineHeight: 1.3,
                }}>
                    Resultados de búsqueda desde la coordenada seleccionada:
                </h3>
            </div>
            <OwnBusinessMissingAlert point={props.point} keyword={props.keyword} />
            <div className="competitors-ranking-table geogrid-point-table" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <GeogridPointTable point={props.point} />
            </div>
        </div>
    );
};

export const GeogridPointDrawer = (props: { point: any, onFinish: any, keyword?: string }) => {
    return (
        <Drawer
            placement="right"
            width={'50%'}
            open={true}
            onClose={props.onFinish}
            bodyStyle={{ background: '#F4F7FA' }}
            headerStyle={{ background: '#F4F7FA' }}
            title={
                <span style={{ color: '#334155', fontWeight: 700 }}>
                    Resultados de búsqueda desde la coordenada seleccionada:
                </span>
            }
        >
            <Card style={{ position: 'relative', margin: '10px', boxSizing: 'border-box', height: '97%', backgroundColor: 'transparent', borderRadius: 10 }}>
                <OwnBusinessMissingAlert point={props.point} keyword={props.keyword} />
                <div className="competitors-ranking-table geogrid-point-table">
                    <GeogridPointTable point={props.point} />
                </div>
            </Card>
        </Drawer>
    );
};

const SEO_COLUMNS: any = [
    {
        title: '',
        dataIndex: 'positionPopularity',
        width: 70,
        render: (text: string, record: any) => {
            const diff = record.positionDiff;
            const hasDiff = diff != null && diff !== 0;
            const position = record.positionPopularity;
            const rankCircle = (
                <div style={{
                    backgroundColor: record.reference ? DOT_COLOR[position] : '#e2e8f0',
                    color: record.reference ? 'white' : '#64748b',
                    height: '35px',
                    width: '35px',
                    borderRadius: '50%',
                    border: record.reference ? 'solid 2px white' : 'solid 1px #cbd5e1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: record.reference ? 700 : 500,
                }}>
                    {position < 21 ? position : <small>{'+20'}</small>}
                </div>
            );

            if (!hasDiff) return rankCircle;

            return (
                <div style={{ position: 'relative', display: 'inline-flex' }}>
                    {rankCircle}
                    <span style={{ position: 'absolute', top: -6, right: -16, zIndex: 1 }}>
                        <DiffLabel diff={diff} solid={!!record.reference} />
                    </span>
                </div>
            );
        }
    },
    {
        title: 'Proximidad',
        dataIndex: 'positionDistance',
        align: 'center',
        width: 150,
        render: (text: string, record: any) => {
            const meters = Number(record.distanceToPoint ?? 0);
            const proximity = meters > 0 ? getDistanceProximity(meters) : null;

            return (
                <div style={{
                    color: record.reference ? '#0f172a' : 'slategrey',
                    lineHeight: 1.3,
                    textAlign: 'center',
                    minWidth: 130,
                }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        flexWrap: 'nowrap',
                    }}>
                        <b style={{ fontSize: 15 }}>
                            {record.positionDistance < 21 ? `${record.positionDistance}º` : '>20º'}
                        </b>
                        {proximity && (
                            <Tag
                                style={{
                                    margin: 0,
                                    borderRadius: 6,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    lineHeight: '18px',
                                    border: '1px solid #94a3b8',
                                    color: '#94a3b8',
                                    background: '#fff',
                                }}
                            >
                                {proximity}
                            </Tag>
                        )}
                    </div>
                    <small style={{
                        display: 'block',
                        marginTop: 2,
                        color: record.reference ? '#64748b' : '#94a3b8',
                    }}>
                        a {meters.toFixed(1)} mts.
                    </small>
                </div>
            );
        }
    },
    {
        title: 'Nombre del negocio',
        dataIndex: 'name',
        render: (text: string, record: any) => (
            <Card
                size="small"
                bordered={false}
                style={{
                    background: record.reference ? 'transparent' : '#f8fafc',
                    borderRadius: 8,
                    boxShadow: 'none',
                    maxWidth: '100%',
                    width: '100%',
                    minWidth: 220,
                }}
                bodyStyle={{ padding: record.reference ? '4px 0' : '8px 10px' }}
            >
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 6,
                    minWidth: 0,
                }}>
                    <OpenStatusColorBadge record={record} />
                    {record.reference ? (
                        <Tag style={{ color: 'white', borderRadius: '20px', margin: 0 }} color="dodgerblue">
                            {record.name}
                        </Tag>
                    ) : (
                        <div style={{
                            fontWeight: 600,
                            color: '#64748b',
                            lineHeight: 1.3,
                            minWidth: 0,
                        }}>
                            {record.name}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12, flexWrap: 'wrap' }}>
                    {record.googleMapsUri && (
                        <a href={record.googleMapsUri} target="blank">Google Maps</a>
                    )}
                    {record.websiteUri && (
                        <a href={record.websiteUri} target="blank">Web</a>
                    )}
                </div>
            </Card>
        )
    },
    {
        title: '',
        key: 'analisis',
        width: 110,
        align: 'center',
        render: (_: any, record: any) => (
            <Tooltip
                title={<BusinessInfoTooltipContent record={record} />}
            >
                <button
                    type="button"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4px 10px',
                        margin: 0,
                        borderRadius: 8,
                        border: '1px solid #e9d5ff',
                        background: '#faf5ff',
                        color: '#7e22ce',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'help',
                        boxShadow: '0 1px 2px rgba(34, 50, 93, 0.04)',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Info
                </button>
            </Tooltip>
        )
    },
]
